import { Box, Flex, Tag, Text } from "@chakra-ui/react"
import { useRouter } from "next/router"
import { Fragment } from "react"
import { Suggestion } from "../../../../api-clients/suggest-openapi"
import { fhAnalyticsUserActions, pages, pdpQueryParams } from "../../../../common/constants/constants"
import { suggestDefaults } from "../../../../common/constants/suggest-constants"
import { useBrandedTranslation } from "../../../../common/hooks/useBrandedTranslation"
import sendFhAnalytics from "../../../../common/lib/fredhopper"
import { redirectToPage } from "../../../../common/utils/router-utils"
import CustomImage from "../../../shared/CustomImage"
import { CInfo } from "../../../shared/Icons"
import PhasingOutIndicator from "../../../shared/PhasingOutIndicator"
import SuggestionRow from "../SuggestionRow"

type Props = {
  suggestions: Array<Suggestion>
  searchTerm: string
  productSuggestions: Array<Suggestion>
  handleResetSearchField: () => void
}

const ArticleSuggestions = ({ suggestions, searchTerm, productSuggestions, handleResetSearchField }: Props) => {
  const router = useRouter()
  const { t } = useBrandedTranslation()

  /* Guard clause if no product suggestions */
  if (!Array.isArray(suggestions) || suggestions.length === 0) {
    return null
  }

  const handleClick = (productId: string, articleNumber: string) => {
    /* Check if we have only one article in the product.
      If so, don't apply the query parameters for highlighting. */
    const productSuggestion = productSuggestions.find((ps) => ps.productid === productId)
    const isSingleArticle = productSuggestion?.amount_articles === "1"

    sendFhAnalytics({
      locale: router.locale,
      action: fhAnalyticsUserActions.SUGG_ARTCLICK,
      itemId: productId,
      shouldBeInterrupted: true,
    }).finally(() => {
      handleResetSearchField()
      //pass search term and article id when redirecting to pdp from article suggestion
      redirectToPage(
        router,
        pages.PRODUCT,
        productId,
        isSingleArticle
          ? ""
          : "?" +
              new URLSearchParams({
                [pdpQueryParams.ARTICLES]: articleNumber,
                [pdpQueryParams.SEARCH_TERM]: searchTerm,
              }).toString()
      )
    })
  }

  return (
    <>
      {suggestions.map((sugg) => {
        const isNew = sugg.novelty_product === "1"
        const isPhasedOut = sugg.phase_out_indicator === "1"
        //Replacement info
        const replacedArticleNumbers = sugg.replaced_articles
          ? sugg.replaced_articles.split(suggestDefaults.REPLACEMENT_INFO_DELIMITER)
          : []

        const replacementForText =
          replacedArticleNumbers.length > 0
            ? `${t("pdp-article-replacementFor")} ${replacedArticleNumbers.join(", ")}`
            : ""
        const replacedByText = sugg.replaced_by
          ? `${t("pdp-article-replacedBy")} ${sugg.replaced_by
              .split(suggestDefaults.REPLACEMENT_INFO_DELIMITER)
              .join(", ")}`
          : ""

        //Replacement info bar text
        const replacedArticle = replacedArticleNumbers.find(
          (articleNumber) => articleNumber === searchTerm || articleNumber.split(".").join("") === searchTerm
        )
        const replacementInfoBarText = replacedArticle
          ? `${t("pdp-article-nubmer")} ${replacedArticle} ${t("suggest-article-replacedBy")} ${sugg.articlenumber}`
          : ""

        //Workaround for images until backend implements _thumbnail attribute
        const thumbnailSrc = sugg._imageurl
          .replace("Large", "Thumbnail")
          .replace("Medium", "Thumbnail")
          .replace("Small", "Thumbnail")

        return (
          <Fragment key={sugg.articlenumber}>
            {replacementInfoBarText && (
              <Flex mb="2" px="2">
                <CInfo mr="3" ml="2" />
                <Text as="span" fontSize="sm" fontWeight="normal" color="gray.900">
                  {replacementInfoBarText}
                </Text>
              </Flex>
            )}
            <SuggestionRow key={sugg.productid} onMouseDown={() => handleClick(sugg.productid, sugg.articlenumber)}>
              <CustomImage src={thumbnailSrc} alt="product suggestion image" minW="10" h="10" mr="4" />
              <Flex w="100%" align="center" justify="space-between">
                <Flex w="100%" flexDirection="column">
                  <Flex w="100%" justify="space-between">
                    <Text mb="1.5" fontSize="sm" fontWeight="medium" className="suggestion-name">
                      {sugg.name}
                    </Text>
                    {isNew && (
                      <Flex gap="2" ml="2" alignSelf="flex-start" justifySelf="flex-end" flexShrink="0">
                        <Tag variant="indicatorNew" size="xs">
                          {t("indicator-new")}
                        </Tag>
                      </Flex>
                    )}
                  </Flex>
                  <Text mb="1.5" fontSize="2xs" color="gray.700">
                    {`${t("pdp-article-nubmer")} ${sugg.articlenumber}`}
                  </Text>
                  {replacementForText && (
                    <Text mb="1.5" fontSize="2xs" color="gray.700">
                      {replacementForText}
                    </Text>
                  )}
                  {replacedByText && (
                    <Text mb="1.5" fontSize="2xs" color="gray.700">
                      {replacedByText}
                    </Text>
                  )}
                  {isPhasedOut && (
                    <Box mt="0.5">
                      <PhasingOutIndicator />
                    </Box>
                  )}
                </Flex>
              </Flex>
            </SuggestionRow>
          </Fragment>
        )
      })}
    </>
  )
}

export default ArticleSuggestions
